import { v4 as uuidv4 } from 'uuid'
import moment from 'moment'
import { pick, values } from 'lodash'
import { exhaustiveAnswersCreate } from '../../../shared/domain/flows/exhaustiveAnswers'
import type { HistoricalReport } from '../../../shared/domain/viewableReports/types.no-deps'
import type { Base_Bus } from '../Base/Base.Bus'
import { I18N_replaceSlug, I18N_replaceNameSlug } from '../I18N/I18N.slugs'
import { IDB_reportWrite, IDB_reportsReadAll } from '../IDB/IDB.table.report'
import { Imag_photosPrune } from '../Imag/Imag.Photo'
import { type Flow_Answer, Flow_generateDefaultAnswers, Flow_hasUnuploadedAnswers } from './Flow.answer'
import { Navi_setTodoSubpage } from '../Navi/Navi.ui.BottomBar'
import { Plan_addEvent, Plan_createEvent } from '../Plan/Plan.addEvent'
import { Rout_toFlowExecutionPage } from '../Rout/Rout.flow'
import type { NReports, Rpts_ClientId } from '../Rpts/Rpts.types'
import { Sync_sync } from '../Sync/Sync.initialise'
import { Todo_doneReportCreate } from '../Todo/Todo.DoneReport'
import type { Todo_ScheduledInstance } from '../Todo/Todo.ScheduledInstance'
import { User_get } from '../User/User.get'
import { getFlowSummary } from '../../../shared/domain/flows/summary.no-deps'
import {
    findAnswer,
    getFileIdsForFileSelectFromAnswers,
    getSubtaskReportsInAnswers,
} from '../../../shared/domain/flows/answer.no-deps'
import type { AppFlowWorkflow, WorkflowReference } from '../../../shared/domain/workflows/types.no-deps'
import { getReportTitle } from '../../../shared/domain/flows/reportTitle.no-deps'
import type { CommonDeps } from '../../../app-core/lib/base/CommonDeps'
import type { FlowReport, FlowContinuationId } from '../../../app-core/lib/flow/FlowReport'
import { Unit_fetchForTypes, Unit_fetchById, Unit_fetchByIds } from '../Unit/Unit.fetch'
import { WF_fetch, WF_resolveReference } from '../WF/WF.fetch'
import { getUnitTypesInFlow } from '../../../shared/domain/flows/unit.no-deps'
import { getFlowUnitIds, getUnitsIdsToFetch } from '../../../shared/domain/flows/FlowUnitsIds.no-deps'
import { whenIdle } from '../../../app-core/lib/util/idle'
import type { KnownUnit } from '../../../app-core/lib/unit/unit'
import { Post_createPostId, type Post_TPost } from '../Post/Post.TPost'
import { isAppActivityWorkflow, isWorkflowForGuide } from '../../../app-core/lib/workflow/Workflow'
import { mapAsync, compact, uniq, map } from '../../../shared/lib/functional/array.no-deps'
import { arrayToMap } from '../../../shared/lib/helpers'
import { IDB_unitTypesReadAll } from '../IDB/IDB.table.unitType'
import { pipe } from '../../../shared/lib/functional/pipe.no-deps'
import { promiseThen } from '../../../shared/lib/functional/promiseThen.no-deps'
import type { CreateSubtaskArgs } from '../../../app-core/lib/flow/ExecutionPageBody'
import type { ClientLogger } from '../../../shared/lib/ClientLogger.no-deps'
import { Logg_createLogger } from '../Logg/Logg.Logger'
import {
    IDB_flowAnswers,
    IDB_flowReports,
    IDB_procedures,
    IDB_reportStatus,
    IDB_update,
    IDB_filesMeta,
    IDB_todoPageTab,
    IDB_scheduledInstances,
} from '../IDB/IDB.dexie'
import { Uplo_releaseGroups } from '../Uplo/Uplo.delete'
import { globalReduxStore } from '../../src/modules/initStore'
import { getCoordinates } from '../../src/redux/reducers'
import { Logg_event } from '../Logg/Logg.event'
import type { ReportClientId } from '../../../shared/domain/reports/types.no-deps'
import { not } from '../../../shared/lib/functional/boolean.no-deps'
import { Flow_readyToStartWorkflow } from './Flow.SubjectType'
import { E2E_waitForResumeReportDone } from '../E2E/E2E.listener'
import type { Nullable, UUID } from '../../../shared/lib/typeHelpers.no-deps'
import { createLoopPlanFromFlow } from '../../../shared/domain/flows/LoopPlan'
import type { Target } from '../../../shared/domain/posts'

import {
    Post_reportPostCreate,
    Post_readReportPost,
    Post_setReportPostInProgress,
    Post_setReportPostTitle,
    Post_setReportPostSubjectUnit,
    Post_setReportPostStepsCount,
} from '../Post/Post.reportPost'

import type {
    ReportId as FlowReportId,
    FlowSummary,
    Answer as FlowAnswer,
    Flow,
} from '../../../shared/domain/flows/types.no-deps'
import type { ReportStatus } from '../../../shared/domain/reportStatus/types.no-deps'
import type * as Guide from '../../../shared/domain/guides/types.no-deps'
import { getGuideElementsContextMap, getRequiredGuideElementsMap } from '../Guid/Guid.helpers'
import { getAllUnitIdsInLoopPlan } from '../../../shared/domain/flows/LoopPlan.no-deps'
import { Training_markAsCompleted } from '../Training/Training_markAsCompleted'
import type { Flow_HandleBackOptions } from './Flow.handleBack'
import { Rout_toItemCompleted } from '../Rout/Rout.todo'
import { IDB_workflowRead } from '../IDB/IDB.table.workflow'
import type { UnitWorkflowParameter } from '../../../shared/domain/UnitWorkflowParameter/types.no-deps'
import { tryUploadNewReport } from './Flow.uploadNewReports'
import { getRecentForProcedureTraining } from '../Rpts/Rpts.getRecentForProcedureTraining'
import type { CamelCaseFlags } from '../../../shared/cfg/flags.no-deps'

interface StartOptions {
    scheduledInstance?: Todo_ScheduledInstance
    teamDoneFor?: number
    subjectUnitId?: number
    previousReport?: FlowContinuationId
    parentClientId?: FlowReportId
    progenitorClientId?: FlowReportId
    parentSubjectUnitId?: number
    comingFromActionScheduledInstance?: boolean
    comingFromScheduledInstance?: UUID
    comingFromUnit?: number
    comingFromWorkflow?: number
    target?: Target
}

async function saveReport(
    logger: ClientLogger,
    procedureId: Nullable<number>,
    workflow: AppFlowWorkflow,
    opts: StartOptions = {},
    requireReportPost: boolean = true
): Promise<FlowReportId> {
    const isGuide: boolean = workflow.type === 'guide'
    const reportClientId = uuidv4() as FlowReportId
    const workflowId = workflow.id
    const postId = Post_createPostId()

    logger.log(`preparing report to save`, { reportClientId, procedureId, workflowId })

    const origin = opts?.scheduledInstance?.origin?.report ?? undefined
    const subjectUnit = opts.subjectUnitId != null ? await Unit_fetchById(logger, opts.subjectUnitId) : null
    const subjectUnitId = opts.subjectUnitId != null ? opts.subjectUnitId : null
    const scheduledInstance =
        opts.scheduledInstance != null
            ? pick(opts.scheduledInstance, 'id', 'dueDate', 'description', 'teams', 'showOverdue', 'parameters')
            : null
    const teamDoneFor = opts.teamDoneFor != null ? opts.teamDoneFor : null
    const previousReport = opts.previousReport != null ? opts.previousReport : null

    // Subtask-specific fields
    const parentClientId = opts.parentClientId != null ? opts.parentClientId : null
    const progenitorClientId = opts.progenitorClientId != null ? opts.progenitorClientId : null
    const parentSubjectUnitId = opts.parentSubjectUnitId != null ? opts.parentSubjectUnitId : null

    const procedure = procedureId === null ? null : await IDB_procedures().get(procedureId)
    const startDate = window.E2E_fakeNow ?? new Date()

    const allUnitTypes = await IDB_unitTypesReadAll()
    const allUnits = await Unit_fetchForTypes(getUnitTypesInFlow(workflow.flow, allUnitTypes))

    const { loopPlan, cachedUnitDataForLoopPlan } = await createLoopPlanFromFlow(
        workflow.flow,
        workflow.workflowParameters ?? [],
        scheduledInstance?.parameters || [],
        Unit_fetchForTypes,
        subjectUnit,
        {}
    )

    const guideElementsContextMap = await getGuideElementsContextMap(logger, workflow.flow)

    let reportStatus: ReportStatus = null
    if (!isGuide) {
        reportStatus = await IDB_reportStatus()
            .where({ workflowId: workflow.id })
            .filter((rs) => rs.isDefault)
            .first()

        if (reportStatus == null) throw new Error("saveReport: Couldn't find reportStatus")
    }

    const locationId = User_get().office

    logger.log(`saving report`, { reportClientId, procedureId, workflowId })

    const { lat, lon } = getCoordinates(globalReduxStore.getState())

    // Guide workflows should be prevented from syncing.
    const doesNotNeedUpdates = workflow.type === 'guide'
    const uploadState = doesNotNeedUpdates
        ? 'neverUpload'
        : Flow_readyToStartWorkflow(workflow, { subjectUnitId })
          ? 'pendingUpload'
          : 'notReadyToUpload'

    const calendarEvent =
        procedure === null
            ? Plan_createEvent(startDate, locationId, workflow.id)
            : Plan_createEvent(startDate, locationId, workflow.id, procedure.id)

    await IDB_flowReports().put({
        id: reportClientId,
        status: 'open',
        procedure:
            procedure === null
                ? null
                : {
                      ...procedure,
                      name: I18N_replaceSlug(procedure.name),
                  },
        workflow: {
            id: workflow.id,
            version: workflow.numberOfVersions,
        },
        title: null,
        scheduledInstance,
        startDate,
        createdBy: User_get().id,
        teamDoneFor,
        origin,
        isAction: opts?.scheduledInstance?.isAction ?? false,
        postId,
        flowUnitIds: getFlowUnitIds(allUnits, workflow.flow),
        loopPlan,
        cachedUnitData: [...cachedUnitDataForLoopPlan],
        // 0 has been added as a default state for the guides in the IDB reference because don't particularly matter and shouldn't overlap with any existing references
        // we can't currently create a report without a report status so for now we have to add this here to prevent errors in the app when executing a guide.
        // See: https://code.zaptic.com/zaptic/zaptic/-/merge_requests/7256
        reportStatusId: reportStatus?.id ?? 0,
        subjectUnitId,
        subjectUnitData: subjectUnit?.data,
        subjectUnitVersion: subjectUnit?.version,
        previousReport,
        parentClientId,
        parentSubjectUnitId,
        progenitorClientId,
        doesNotNeedUpdates,
        uploadState,
        collaborators: { invited: [], accepted: [User_get().id] },
        lastSyncTime: new Date(0),
        coordinates: { lat, lon },
        location: locationId,
        calendarEvent,
    })

    // Only create a report post if required e.g for guides we dont want any report post
    if (requireReportPost) {
        await Post_reportPostCreate(logger, {
            postId,
            reportClientId,
            title: null,
            locationId,
            procedure,
            workflow,
            startDate,
            stepsCompleted: 0,
            reportStatusId: reportStatus?.id,
            subjectUnitId,
            reportParentClientId: parentClientId,
            inProgress: true,
        })
    }

    logger.log(`saved report`, { reportClientId, procedureId, workflowId })

    await pipe(
        Flow_generateDefaultAnswers(
            reportClientId,
            workflow.flow,
            startDate,
            allUnits,
            loopPlan,
            guideElementsContextMap,
            subjectUnitId
        ),
        map((answer): Flow_Answer => ({ ...answer, uploadState: 'pendingUpload' })),
        (answers) => IDB_flowAnswers().bulkAdd(answers)
    )

    logger.log(`saved report's default answers`, { reportClientId, procedureId, workflowId })

    return reportClientId
}

export async function Flow_reportStart(
    logger: ClientLogger,
    procedureId: number,
    workflow: AppFlowWorkflow,
    opts: StartOptions = {}
) {
    const reportClientId = await saveReport(logger, procedureId, workflow, opts)

    logger.log(`navigating to new report`, { reportClientId, sectionId: workflow.flow.id })
    Rout_toFlowExecutionPage(reportClientId, {
        sectionId: workflow.flow.id,
        comingFromActionScheduledInstance: opts.comingFromActionScheduledInstance,
        comingFromScheduledInstance: opts.comingFromScheduledInstance,
        comingFromUnit: opts.comingFromUnit,
        comingFromWorkflow: opts.comingFromWorkflow,
    })
}

export async function createGuideReportId(logger: ClientLogger, workflow: AppFlowWorkflow, opts: StartOptions = {}) {
    // Check any existing report(s) for this guide workflow
    const openGuideReportIds = (await IDB_flowReports().toArray())
        ?.filter(
            (flowReport) =>
                flowReport.workflow.id === workflow.id &&
                flowReport.status === 'open' &&
                flowReport.uploadState === 'neverUpload'
        )
        .map((openGuideReport) => openGuideReport.id)

    // If there is any report, delete it as we only want 1 report for a guide
    if (openGuideReportIds.length) {
        logger.log(`found ${openGuideReportIds.length} guide/s reports to delete`)

        // Delete existing report for this guide workflow
        const deletedReportsCount = await IDB_flowReports().where('id').anyOf(openGuideReportIds).delete()

        logger.log(`deleted ${deletedReportsCount} guides reports`)
    }

    // Create new report id for guide execution, but don't create a post
    const reportId = await saveReport(logger, null, workflow, opts, false)

    return reportId
}

async function finaliseReport(
    logger: ClientLogger,
    reportClientId: FlowReportId,
    locationId: number,
    existingReports: Rpts_ClientId[],
    featureFlags: Pick<CamelCaseFlags, 'appendToReportTitle'>
) {
    logger.log(`finalising report`, { reportClientId })

    const flowReport = await Flow_fetchFullReport(logger, reportClientId, featureFlags)
    const { flowSummary, workflow } = flowReport

    const completedAnswer = findAnswer(flowSummary.answers, workflow.flow.id, [])

    /* This is now only for legacy reports from before multi-user reports */
    if (!existingReports.includes(flowReport.id) && flowReport.collaborators == null) {
        // We should track this to see if it's safe to remove this code
        Logg_event('LegacyFinalizeReport')

        // Events are created before reports so if there is a report we can
        // reason that there is also an event
        const event = await Plan_addEvent(logger, new Date(), locationId, workflow.id, flowReport.procedure.id)

        const finalReport: NReports.TIntermediateReport = {
            title: flowReport.title,
            clientId: flowReport.id,
            location: locationId,
            event,
            startDate: flowReport.startDate.toISOString(),
            endDate: flowSummary.completedDate.toISOString(),
            coordinates: {
                lat: completedAnswer.latitude || 0,
                lon: completedAnswer.longitude || 0,
            },
            reportedActionList: {
                actionList: workflow.id,
                reportedActions: [],
            },
            uploaded: false,
            answers: values(exhaustiveAnswersCreate(workflow.flow, flowSummary)),
            flowUnitIds: flowReport.flowUnitIds,
            subjectUnitId: flowReport.subjectUnitId,
            subjectUnitVersion: flowReport.subjectUnitVersion,
            workflowVersion: flowReport.workflow.numberOfVersions,
            parentClientId: flowReport.parentClientId,
        }

        if (flowReport.scheduledInstance != null) {
            finalReport.scheduledInstance = flowReport.scheduledInstance.id
        }
        if (flowReport.parentClientId != null) {
            finalReport.parentClientId = flowReport.parentClientId
        }

        // This table is keyed on the numerical id we get from the server so we
        // cannot rely on indexeddb to deduplicate based on its key, hense the
        // if statement above
        await IDB_reportWrite(logger, finalReport)

        // Either delete uploads or mark them as deletable later
        await Uplo_releaseGroups(logger, [reportClientId])
    }

    const createArgs = {
        reportClientId: flowReport.id,
        title: flowReport.title,
        origin: flowReport.origin,
        isAction: flowReport.isAction,
        locationId,
        procedure: flowReport.procedure,
        workflow,
        startDate: flowReport.startDate,
        endDate: flowSummary.completedDate,
        scheduledInstance: flowReport.scheduledInstance,
        teamDoneFor: flowReport.teamDoneFor,
        stepsCompleted: flowSummary.steps.length,
        reportStatusId: flowReport.reportStatusId,
        subjectUnitId: flowReport.subjectUnitId,
        reportParentClientId: flowReport.parentClientId ? flowReport.parentClientId : null,
        inProgress: false,
    }

    // Done reports are keyed on client id so we don't have to de-duplicate
    await Todo_doneReportCreate(logger, createArgs)

    // Find existing report post
    let post = await Post_readReportPost(logger, flowReport.id)
    if (post != null) {
        // Update the existing report post
        await Post_setReportPostInProgress(reportClientId, false)
        await Post_setReportPostStepsCount(flowReport.id, flowSummary.steps.length)
    } else {
        // If missing, create a new one now
        post = await Post_reportPostCreate(logger, createArgs)
    }

    await Promise.all(
        flowReport.subtasksReports
            .filter((subtask) => subtask.status === 'completed')
            .map((subtask) => finaliseReport(logger, subtask.id, locationId, existingReports, featureFlags))
    )
    logger.log(`sub tasks finalised`, { reportClientId, subTasksCount: flowReport.subtasksReports.length })

    return { flowReport, post }
}

export async function Flow_prepareReportForCompletion(
    reportClientId: FlowReportId,
    featureFlags: Pick<CamelCaseFlags, 'appendToReportTitle'>
) {
    const logger = Logg_createLogger('Flow_prepareReportForCompletion')

    if (window.E2E_PauseReportDone) await E2E_waitForResumeReportDone()

    const locationId = User_get().office
    logger.log(`found single location`, { reportClientId, locationId })

    const reportsThatAlreadyExist = await IDB_reportsReadAll().then((reports) =>
        reports.map((r) => r.clientId as Rpts_ClientId)
    )
    logger.log(`found previous reports`, { reportClientId, reportsThatAlreadyExist })

    const { flowReport, post } = await finaliseReport(
        logger,
        reportClientId,
        locationId,
        reportsThatAlreadyExist,
        featureFlags
    )

    // Check if the completed answer has already been uploaded
    // If so we want the report to go into pendingFinalisation uploadState
    const readyToBeFinalised = await Flow_hasUnuploadedAnswers(flowReport.id).then(not)

    await markReportAsCompleted(logger, flowReport.id, flowReport.flowSummary, readyToBeFinalised)

    return { flowReport, post }
}

export async function Flow_navigateToDoneReport(
    reportClientId: FlowReportId,
    bus: Base_Bus,
    flowReport: Flow_FullReport,
    post: Post_TPost,
    opts?: Flow_HandleBackOptions
) {
    const logger = Logg_createLogger('Flow_navigateToDoneReport')
    await IDB_todoPageTab().set('todo')

    // Do not allow user to go back to report via the bottom bar
    Navi_setTodoSubpage(null)

    Rout_toItemCompleted(
        flowReport.workflow.type === 'training' ? 'training' : 'task',
        flowReport.scheduledInstance?.id,
        post.id,
        flowReport.origin,
        flowReport.procedure?.id,
        flowReport.previousReport,
        opts
    )

    logger.log(`waiting for idle`, { reportClientId })
    await whenIdle()

    logger.log(`syncing`, { reportClientId })
    await Sync_sync('Flow report completed', bus)

    logger.log(`pruning photos`, { reportClientId })
    await Imag_photosPrune()

    logger.log(`done`, { reportClientId })
}

export async function Flow_reportDone(
    reportClientId: FlowReportId,
    bus: Base_Bus,
    featureFlags: Pick<CamelCaseFlags, 'appendToReportTitle'>,
    opts?: Flow_HandleBackOptions
) {
    const { flowReport, post } = await Flow_prepareReportForCompletion(reportClientId, featureFlags)

    // If the completed report belongs to a training, we want to mark it as complete separately.
    if (flowReport.workflow.type === 'training') {
        await Training_markAsCompleted(flowReport.workflow.id)
    }

    await Flow_navigateToDoneReport(reportClientId, bus, flowReport, post, opts)
}

export async function markReportAsCompleted(
    logger: ClientLogger,
    reportClientId: FlowReportId,
    flowSummary: FlowSummary,
    readyToBeFinalised?: boolean
) {
    logger.log(`marking report as completed`, { reportClientId })

    const flowReport = await IDB_flowReports().get(reportClientId)
    let uploadState = flowReport.uploadState

    // Work out which uploadState we need to be in if we want to finalise
    // If we haven't uploaded yet, we don't want to forget to do that
    if (readyToBeFinalised) {
        uploadState =
            flowReport.uploadState === 'pendingUpload' ? 'pendingUploadAndFinalisation' : 'pendingFinalisation'
    }

    // Save updated flow report to disk
    await IDB_update(IDB_flowReports(), reportClientId, {
        status: 'completed',
        completedDate: flowSummary.completedDate,
        uploadState,
    })

    logger.log(`marked report as completed`, { reportClientId })
}

export async function markReportAsIncomplete(logger: ClientLogger, reportClientId: FlowReportId) {
    logger.log(`marking report as incompleted`, { reportClientId })

    await IDB_update(IDB_flowReports(), reportClientId, {
        status: 'open',
        completedDate: null,
    })

    logger.log(`marked report as incompleted`, { reportClientId })
}

export async function Flow_reportsPrune() {
    const logger = Logg_createLogger(`Flow_reportsPrune`)

    const twoDaysAgo = moment().subtract(2, 'days').toDate()

    const completedReports = await IDB_flowReports().where('completedDate').below(twoDaysAgo).primaryKeys()
    const cancelledReports = await IDB_flowReports().where('cancelledDate').below(twoDaysAgo).primaryKeys()
    const unassignedReports = await IDB_flowReports().where('unassignedDate').below(twoDaysAgo).primaryKeys()

    // Retain recent training reports to prevent deletion
    const recentTrainingReports = await getRecentForProcedureTraining()
    const completedReportsToKeep: ReportClientId[] = recentTrainingReports.flatMap((data) =>
        data.training.map((t) => t.report.clientId)
    )
    const reportsToDelete = completedReports.filter((r) => !completedReportsToKeep.includes(r))

    const allReports = [...reportsToDelete, ...cancelledReports, ...unassignedReports]
    logger.log(`found reports to delete`, { count: allReports?.length, twoDaysAgo })

    const deleted = await IDB_flowReports().where('id').anyOf(allReports).delete()
    logger.log(`deleted reports`, { count: deleted })

    const deletedAnswers = await IDB_flowAnswers().where('report').anyOf(allReports).delete()
    logger.log(`deleted report answers`, { count: deletedAnswers })

    // Either delete uploads or mark them as deletable later
    await Uplo_releaseGroups(logger, allReports)
}

/**
 * Report fetched from idb with 1:1 joins fetched
 */
export interface Flow_FetchedReport extends FlowReport<AppFlowWorkflow> {
    subjectUnit: KnownUnit
    dueDate: Date
}

/**
 * FullReport includes 1:many joins and derived data from answers
 */
export interface Flow_FullReport extends Flow_FetchedReport {
    flowAnswers: FlowAnswer[]
    flowSummary: FlowSummary
    subtasksReports: Flow_FetchedReport[]
}

export async function Flow_fetchReports(
    logger: ClientLogger,
    reportIds: FlowReportId[]
): Promise<Flow_FetchedReport[]> {
    const reports = await pipe(
        reportIds,
        uniq,
        mapAsync((id) => Flow_fetchReport(logger, id)),
        promiseThen(compact)
    )
    return reports
}

export async function Flow_fetchReport(
    logger: ClientLogger,
    reportClientId: FlowReportId
): Promise<Flow_FetchedReport> {
    const report = await IDB_flowReports().get(reportClientId)
    if (report == null) {
        logger.warn(`could not find report`, { reportClientId })
        return null
    }
    const procedure = report.procedure === null ? null : I18N_replaceNameSlug(report.procedure)
    const workflow = await WF_resolveReference(logger, report.workflow).then(I18N_replaceNameSlug)
    const subjectUnit = report.subjectUnitId != null && (await Unit_fetchById(logger, report.subjectUnitId))
    const dueDate = report.scheduledInstance?.dueDate
    return { ...report, procedure, workflow, subjectUnit, dueDate }
}

export async function Flow_fetchAnswers(reportId: FlowReportId) {
    return IDB_flowAnswers().where({ report: reportId }).toArray()
}

export async function Flow_fetchReportHistory(deps: CommonDeps, reportId: number): Promise<HistoricalReport[]> {
    const { reports } = await deps.httpClient.getReportHistory(reportId, {
        includeRequestedReport: true,
        includeLanguage: true,
    })
    return reports
}

// Helper function to get file meta data for report answers of type file select
export async function Flow_fetchFilesMetaData(reportId: FlowReportId) {
    // Get report answers from indexed db
    const answers = await IDB_flowAnswers().where({ report: reportId }).toArray()

    // Get file select answer ids
    const fileSelectAnswersIds = getFileIdsForFileSelectFromAnswers(answers)

    // Get file meta data from indexed db for the given ids
    const fileMetaData = await IDB_filesMeta().where('fileId').anyOf(fileSelectAnswersIds).toArray()

    // Create a map
    const filesMetaDataMap = pipe(fileMetaData, (a) => arrayToMap(a, 'fileId'))

    return filesMetaDataMap
}

export function Flow_fetchSubtaskReports(
    logger: ClientLogger,
    flowAnswers: FlowAnswer[]
): Promise<Flow_FetchedReport[]> {
    return pipe(flowAnswers, getSubtaskReportsInAnswers, (ids) => Flow_fetchReports(logger, ids))
}

export function Flow_fetchSubtaskReportMap(
    logger: ClientLogger,
    flowAnswers: FlowAnswer[]
): Promise<Map<FlowReportId, Flow_FetchedReport>> {
    return Flow_fetchSubtaskReports(logger, flowAnswers).then((array) => arrayToMap(array, 'id'))
}

export async function Flow_getSummary(
    report: Flow_FetchedReport,
    flowAnswers: FlowAnswer[],
    requiredGuidesMap: Map<Guide.Id, Pick<Guide.SyncGuide, 'isDeleted'>>,
    guideElementsContextMap: Map<Guide.Id, { guide: Guide.SyncGuide; workflow: WorkflowReference }>,
    subtaskReports: Flow_FetchedReport[]
) {
    const subtaskMap = arrayToMap(subtaskReports, 'id')
    const isSubtaskOpen = (reportId: FlowReportId) => subtaskMap.get(reportId)?.status === 'open'
    const unitIdsRelevantForFlow = pipe(
        [...getUnitsIdsToFetch(report.flowUnitIds), ...getAllUnitIdsInLoopPlan(report.loopPlan ?? [])],
        uniq
    )
    const unitsRelevantForFlow = await Unit_fetchByIds(unitIdsRelevantForFlow)

    return getFlowSummary(
        report.workflow.flow,
        flowAnswers,
        report.startDate,
        report.subjectUnitData,
        isSubtaskOpen,
        report.loopPlan,
        unitsRelevantForFlow,
        report.cachedUnitData,
        requiredGuidesMap,
        guideElementsContextMap
    )
}

export async function Flow_fetchFullReport(
    logger: ClientLogger,
    reportClientId: FlowReportId,
    featureFlags: Pick<CamelCaseFlags, 'appendToReportTitle'>
): Promise<Flow_FullReport> {
    logger.log(`fetching full report`, { reportClientId })

    const report = await Flow_fetchReport(logger, reportClientId)
    if (report == null) return null

    logger.log(`fetching answers`, { reportClientId })
    const flowAnswers = await Flow_fetchAnswers(report.id)

    logger.log(`fetching subtasks`, { reportClientId })
    const subtasksReports = await Flow_fetchSubtaskReports(logger, flowAnswers)

    logger.log(`creating map for flow's required guide elements`, { reportClientId })
    const requiredGuidesMap = await getRequiredGuideElementsMap(logger, report.workflow.flow)

    logger.log(`creating map for flow's guide elements context`, { reportClientId })
    const guideElementsContextMap = await getGuideElementsContextMap(logger, report.workflow.flow)

    logger.log(`deriving summary`, { reportClientId })
    const flowSummary = await Flow_getSummary(
        report,
        flowAnswers,
        requiredGuidesMap,
        guideElementsContextMap,
        subtasksReports
    )

    // Re-deriving already present title for migration reasons
    const title = getReportTitle(
        report.workflow.flow,
        flowSummary,
        report.workflow.name,
        featureFlags.appendToReportTitle
    )

    logger.log(`fetched full report`, { reportClientId })
    return { ...report, flowAnswers, subtasksReports, flowSummary, title }
}

export async function Flow_fetchOpenReports(
    logger: ClientLogger,
    featureFlags: Pick<CamelCaseFlags, 'appendToReportTitle'>
) {
    logger.log(`fetching open reports`)

    const openReports = await IDB_flowReports()
        .where('status')
        .equals('open')
        .filter((report) => report.parentClientId == null)
        .primaryKeys()
        .then(mapAsync((id) => Flow_fetchFullReport(logger, id, featureFlags)))

    logger.log(`fetched open reports`, { count: openReports.length })
    return openReports
}

export async function Flow_createSubTask(args: CreateSubtaskArgs): Promise<FlowReportId> {
    const logger = Logg_createLogger('Flow_createSubTask')

    const workflow = await WF_fetch(logger, args.workflowId, { includeDeleted: false })

    if (isAppActivityWorkflow(workflow)) {
        throw logger.error(new Error(`Workflow is an ActivityWorkflow. AppFlowWorkflow was expected`), {
            workflowId: args.workflowId,
        })
    }

    // Create new report, but don't create a post if it is a guide
    return saveReport(
        logger,
        args.procedureId,
        workflow,
        {
            parentClientId: args.parentClientId,
            progenitorClientId: args.progenitorClientId,
            subjectUnitId: args.subjectUnitId,
            parentSubjectUnitId: args.parentSubjectUnitId,
        },
        !isWorkflowForGuide(workflow)
    )
}

export async function Flow_cancelReport(reportClientId: FlowReportId) {
    const logger = Logg_createLogger('Flow_cancelReport')
    const cancelledDate = new Date()

    // Find the descendants
    const descendants = await Flow_findDescendantReports(logger, reportClientId)
    if (descendants == null) return

    // Mark the top level report as needed to be cancelled on the server
    // The children will get cancelled by the server at the same time
    await IDB_update(IDB_flowReports(), reportClientId, {
        status: 'cancelled',
        cancelledDate,
        uploadState: 'pendingCancel',
    })

    // Cancel the subtasks
    await IDB_flowReports().where('id').anyOf(descendants).modify({
        status: 'cancelled',
        cancelledDate,
    })

    logger.log('report cancelled', { reportClientId, descendants })

    // Either delete uploads or mark them as deletable later
    await Uplo_releaseGroups(logger, [reportClientId, ...descendants])
}

export async function Flow_findDescendantReports(logger: ClientLogger, reportClientId: FlowReportId) {
    // Fetch the report
    const flowReport = await IDB_flowReports().get(reportClientId)
    if (flowReport == null) {
        logger.warn('could not find report')
        return null
    }

    // Fetch all the subtasks reports in family
    const progenitorClientId = flowReport.progenitorClientId ?? reportClientId
    const subtasks = await IDB_flowReports().where({ progenitorClientId }).sortBy('startDate')

    // Find the ones that are the children of selected report
    const descendants = new Set<ReportClientId>()
    for (const subtask of subtasks) {
        if (!descendants.has(subtask.parentClientId)) continue
        descendants.add(subtask.id)
    }

    return [...descendants]
}

export async function Flow_saveTitle(reportClientId: FlowReportId, title: string) {
    await IDB_update(IDB_flowReports(), reportClientId, { title })
    await Post_setReportPostTitle(reportClientId, title)
}

export async function Flow_saveUnitSubject(reportClientId: FlowReportId, flow: Flow, unit: KnownUnit) {
    const logger = Logg_createLogger('Flow_saveUnitSubject')

    const report = await IDB_flowReports().get(reportClientId)
    const workflowId = report.workflow.id
    const reportWorkflow = (await IDB_workflowRead(workflowId)) as AppFlowWorkflow
    const workflowParameters = reportWorkflow?.workflowParameters ?? []

    let unitWorkflowParameters: UnitWorkflowParameter[] = []
    if (report?.scheduledInstance) {
        const scheduledInstance = await IDB_scheduledInstances().get(report?.scheduledInstance)
        unitWorkflowParameters = scheduledInstance?.parameters ?? []
    }

    const { loopPlan, cachedUnitDataForLoopPlan } = await createLoopPlanFromFlow(
        flow,
        workflowParameters,
        unitWorkflowParameters,
        Unit_fetchForTypes,
        unit,
        {}
    )
    await IDB_update(IDB_flowReports(), reportClientId, {
        uploadState: 'pendingUpload',
        subjectUnitId: unit.id,
        subjectUnitData: unit.data,
        subjectUnitVersion: unit.version,
        loopPlan,
        cachedUnitData: [...cachedUnitDataForLoopPlan],
    })
    await Post_setReportPostSubjectUnit(reportClientId, unit.id)
    await tryUploadNewReport(logger, reportClientId)
}

export async function Flow_inviteCollaborators(reportClientId: FlowReportId, collaborators: number[]) {
    await IDB_flowReports()
        .where({ id: reportClientId })
        .modify((flowReport) => {
            if (flowReport.collaborators == null) return
            flowReport.collaborators.invited.push(...collaborators)
        })
}
